import { useMutation, useQuery } from '@apollo/client'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { Autocomplete, Button, Grid, TextField } from '@mui/material'
import { gql } from '__generated__'
import { StafferEntry } from '__generated__/graphql'
import { useFormik } from 'formik'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import DrawerComponent from 'shared/components/DrawerComponent'
import NonFormikPhoneInput from 'shared/components/NonFormikPhoneInput'
import useToast from 'shared/components/Toast/useToast'
import { GetTitleOptions } from 'shared/graphql/titles.gql'
import { formatGraphQLError } from 'shared/utils'
import * as Yup from 'yup'
import { GetCandidateStaffers, Staffer } from './CandidateStafferPanel'

const AddCandidateStaffer = gql(/* GraphQL */ `
    mutation AddCandidateStaffer($input: StafferEntry!, $candidateId: ID!) {
        addCandidateStaffer(input: $input, candidateId: $candidateId) {
            success
            message
        }
    }
`)

const UpdateCandidateStaffer = gql(/* GraphQL */ `
    mutation UpdateCandidateStaffer($input: StafferEntry!, $id: ID!) {
        updateCandidateStaffer(input: $input, id: $id) {
            success
            message
        }
    }
`)

const DeleteCandidateStaffer = gql(/* GraphQL */ `
    mutation DeleteCandidateStaffer($id: ID!) {
        deleteCandidateStaffer(id: $id) {
            success
            message
        }
    }
`)

interface FormValues extends StafferEntry {}

interface CandidateStafferDrawerProps {
    isOpen: boolean
    onClose: () => void
    onCancel: () => void
    staffers: Staffer[]
}

export const CandidateStafferDrawer = ({
    isOpen,
    onClose,
    onCancel,
    staffers,
}: CandidateStafferDrawerProps) => {
    const { candidateId } = useParams()
    const toast = useToast()

    const { data: titles, loading: titlesLoading } = useQuery(GetTitleOptions)

    const [addCandidateStaffer] = useMutation(AddCandidateStaffer, {
        refetchQueries: [GetCandidateStaffers],
        onCompleted: (data) => {
            if (data.addCandidateStaffer.success) {
                toast({
                    title: 'Success!',
                    message: 'Candidate staffer added successfully',
                    severity: 'success',
                })

                onClose()
            } else {
                toast({
                    title: 'Error',
                    message: `Failed to add candidate staffer: ${data.addCandidateStaffer.message}`,
                    severity: 'error',
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(error, 'Something went wrong.'),
                severity: 'error',
            })
        },
    })

    const [updateCandidateStaffer] = useMutation(UpdateCandidateStaffer, {
        refetchQueries: [GetCandidateStaffers],
        onCompleted: (data) => {
            if (data.updateCandidateStaffer.success) {
                toast({
                    title: 'Success!',
                    message: 'Candidate staffer updated successfully',
                    severity: 'success',
                })

                onClose()
            } else {
                toast({
                    title: 'Error',
                    message: `Failed to update candidate staffer: ${data.updateCandidateStaffer.message}`,
                    severity: 'error',
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(error, 'Something went wrong.'),
                severity: 'error',
            })
        },
    })

    const [deleteCandidateStaffer] = useMutation(DeleteCandidateStaffer, {
        refetchQueries: [GetCandidateStaffers],
        onCompleted: (data) => {
            if (data.deleteCandidateStaffer.success) {
                toast({
                    title: 'Success!',
                    message: 'Candidate staffer deleted successfully',
                    severity: 'success',
                })

                formik.resetForm()
                onClose()
            } else {
                toast({
                    title: 'Error',
                    message: `Failed to delete candidate staffer: ${data.deleteCandidateStaffer.message}`,
                    severity: 'error',
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(error, 'Something went wrong.'),
                severity: 'error',
            })
        },
    })

    const initialValues = useMemo(
        () => ({
            firstName: staffers[0]?.firstName || '',
            lastName: staffers[0]?.lastName || '',
            email: staffers[0]?.email || '',
            phone: staffers[0]?.phone || '',
            titleId: staffers[0]?.title?.id || '',
        }),
        [staffers]
    )

    const validationSchema = Yup.object({
        firstName: Yup.string().required('First name is required'),
        lastName: Yup.string().required('Last name is required'),
        titleId: Yup.string().required('Title is required'),
        email: Yup.string()
            .email('Invalid email')
            .required('Email is required'),
        phone: Yup.string(),
    })

    const formik = useFormik<FormValues>({
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            if (staffers.length > 0) {
                handleUpdateStaffer(values)
            } else {
                handleAddStaffer(values)
            }
        },
        enableReinitialize: true,
    })

    const handleAddStaffer = (values: FormValues) => {
        addCandidateStaffer({
            variables: {
                candidateId: candidateId || '',
                input: values,
            },
        })
    }

    const handleUpdateStaffer = (values: FormValues) => {
        updateCandidateStaffer({
            variables: {
                id: staffers[0]?.id || '',
                input: values,
            },
        })
    }

    const handleDeleteStaffer = () => {
        deleteCandidateStaffer({
            variables: { id: staffers[0]?.id || '' },
        })
    }

    const handleClose = () => {
        onCancel()
        onClose()
    }

    return (
        <DrawerComponent
            open={isOpen}
            anchor="right"
            onClose={handleClose}
            hasCancelButton
            onCancel={handleClose}
            primaryActionText={staffers.length > 0 ? 'Update' : 'Add'}
            saveButtonDisabled={!formik.dirty}
            onPrimaryAction={formik.handleSubmit}
            headerTitle="Staffer Information"
        >
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                        }
                        helperText={
                            formik.touched.firstName && formik.errors.firstName
                        }
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                        }
                        helperText={
                            formik.touched.lastName && formik.errors.lastName
                        }
                        required
                    />
                </Grid>

                <Grid item xs={12}>
                    <Autocomplete
                        loading={titlesLoading}
                        value={
                            titles?.titles?.find(
                                (title) => title.id === formik.values.titleId
                            ) || null
                        }
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        onChange={(e, value) => {
                            formik.setFieldValue('titleId', value?.id || '')
                        }}
                        options={titles?.titles || []}
                        getOptionLabel={(option) => option.value || ''}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Title"
                                placeholder="Select a Title"
                                error={
                                    formik.touched.titleId &&
                                    Boolean(formik.errors.titleId)
                                }
                                helperText={
                                    formik.touched.titleId &&
                                    formik.errors.titleId
                                }
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <NonFormikPhoneInput
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={formik.values.phone ?? ''}
                        handleInputChange={(value) => {
                            formik.setFieldValue('phone', value || '')
                        }}
                        error={
                            formik.touched.phone && Boolean(formik.errors.phone)
                        }
                        helperText={formik.touched.phone && formik.errors.phone}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={
                            formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                        required
                    />
                </Grid>

                {staffers.length > 0 && (
                    <Grid item xs={12}>
                        <Button
                            onClick={handleDeleteStaffer}
                            startIcon={<DeleteIcon />}
                            variant="text"
                            color="error"
                        >
                            Delete Staffer
                        </Button>
                    </Grid>
                )}
            </Grid>
        </DrawerComponent>
    )
}
